import { useAppDispatch } from "@/config/configureAppStore";
import { MIXPANEL_EVENTS } from "@/constants/mixpanel";
import { setNotificationPermissionDialog } from "@/reducers/slices/dialogSlice/dialogSlice";
import { trackMixpanelEvent } from "@/utils/mixpanel";
import { storageService } from "@/utils/storage";
import { useCallback, useEffect } from "react";

const NOTIFICATION_PERMISSION_KEY = "notification_permission";

interface NotificationOptions extends Partial<typeof window.Notification> {
  title: string;
  body: string;
  onClick: () => void;
  tag?: string;
  data?: Record<string, string>;
}

const options = {
  badge:
    "https://cdn.prod.website-files.com/66b3765153a8a0c399c70981/67065c15ad03044508747008_favicon-32x32.png",
  // Shows at the bottom right corner
  icon: "https://cdn.prod.website-files.com/66b3765153a8a0c399c70981/67065c15ad03044508747008_favicon-32x32.png",
  lang: "en-US"
};

const useDesktopNotifications = () => {
  const dispatch = useAppDispatch();

  const showNotification = useCallback(
    ({
      title,
      body,
      onClick,
      tag = "default",
      ...rest
    }: NotificationOptions) => {
      const notification = new Notification(title, {
        ...options,
        body,
        tag, // Used to replace previous notifications with the same tag
        silent: false,
        ...rest
      });
      notification.onclick = () => {
        window.focus();
        onClick();
      };
      notification.onclose = () => {
        console.log("Notification closed");
      };
      notification.onerror = () => {
        console.log("Notification error");
      };
      notification.onshow = () => {
        console.log("Notification shown");
      };
    },
    []
  );

  const askPermission = useCallback(() => {
    Notification.requestPermission().then((permission) => {
      storageService.setItem(NOTIFICATION_PERMISSION_KEY, permission);
      console.debug("Notification permission", permission);
      trackMixpanelEvent(MIXPANEL_EVENTS.NOTIFICATION_PERMISSION_VALUE, {
        value: permission
      });
    });
  }, []);

  const getNotificationPermission = useCallback(() => {
    if (!("Notification" in window)) {
      console.debug("This browser does not support desktop notification");
      return;
    }
    const storageValue = storageService.getItem(NOTIFICATION_PERMISSION_KEY);
    const grantedValues = ["granted", "denied"];
    const isPermissionAsked =
      (storageValue && grantedValues.includes(storageValue)) ||
      grantedValues.includes(Notification.permission);

    if (isPermissionAsked) {
      return;
    }

    if (Notification.permission === "granted") {
      console.log("Notification permission granted");
    } else if (Notification.permission !== "denied") {
      console.log("Notification permission not denied. Asking for permission");
      trackMixpanelEvent(MIXPANEL_EVENTS.NOTIFY_ME_START);
      dispatch(setNotificationPermissionDialog(true));
    }
  }, [dispatch]);

  useEffect(() => {
    const permit = Notification.permission;
    const isPermissionGranted = storageService.getItem(
      NOTIFICATION_PERMISSION_KEY
    );
    if ((!permit || permit === "default") && isPermissionGranted) {
      storageService.removeItem(NOTIFICATION_PERMISSION_KEY);
    } else {
      storageService.setItem(NOTIFICATION_PERMISSION_KEY, permit);
    }
  }, []);

  return { getNotificationPermission, showNotification, askPermission };
};

export default useDesktopNotifications;
